import { printerDebug, printerDebugWarn } from './printerDebug';

function formatNotifyHex(value: DataView): string {
  const n = Math.min(value.byteLength, 16);
  const parts: string[] = [];
  for (let i = 0; i < n; i += 1) {
    parts.push(value.getUint8(i).toString(16).padStart(2, '0'));
  }
  return parts.join(' ');
}

export type IsscBootstrapResult = {
  credits: number;
  /** True when no [0x01, n] notify arrived — use delay pacing instead of credit gating. */
  usedFallback: boolean;
};

/**
 * ISSC / Marklife-style BLE UART credit flow: printer grants credits via notify
 * [0x01, count] before the bridge accepts more host→peripheral writes.
 */
export class IsscCreditGate {
  private credits = 0;

  private sawRealCreditNotify = false;

  private readonly waiters: Array<() => void> = [];

  private starvationTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  /** Call from characteristicvaluechanged on ISSC notify (1e4d…). */
  onNotifyValue(value: DataView): void {
    if (value.byteLength < 1) return;
    printerDebug('ISSC notify raw', {
      byteLength: value.byteLength,
      hex: formatNotifyHex(value),
    });
    if (value.byteLength < 2) return;
    const b0 = value.getUint8(0);
    const b1 = value.getUint8(1);
    if (b0 === 0x01) {
      this.sawRealCreditNotify = true;
      this.credits += b1;
      printerDebug('ISSC credit grant', { added: b1, total: this.credits });
      this.flushWaiters();
    }
  }

  usesRealCreditFlow(): boolean {
    return this.sawRealCreditNotify;
  }

  /** Prime credits if the printer never sends 0x01 (compat). */
  grantInitialCredit(count = 1): void {
    this.credits += count;
    printerDebug('ISSC credit initial grant', { count, total: this.credits });
    this.flushWaiters();
  }

  getCredits(): number {
    return this.credits;
  }

  /** Wait for printer to send [0x01, n] after notify subscribe. */
  async bootstrapCredits(waitMs = 1200, fallbackCredits = 4): Promise<IsscBootstrapResult> {
    const start = Date.now();
    while (Date.now() - start < waitMs) {
      if (this.sawRealCreditNotify && this.credits > 0) {
        printerDebug('ISSC credits after notify bootstrap', { credits: this.credits });
        return { credits: this.credits, usedFallback: false };
      }
      await new Promise<void>((r) => {
        globalThis.setTimeout(r, 40);
      });
    }
    if (!this.sawRealCreditNotify) {
      this.grantInitialCredit(fallbackCredits);
      printerDebugWarn('ISSC using delay-only pacing (no 0x01 credit notifies)', {
        fallbackCredits,
      });
      return { credits: this.credits, usedFallback: true };
    }
    return { credits: this.credits, usedFallback: false };
  }

  private flushWaiters(): void {
    while (this.credits > 0 && this.waiters.length > 0) {
      const w = this.waiters.shift();
      w?.();
    }
  }

  private consumeOne(): void {
    if (this.credits > 0) this.credits -= 1;
  }

  /**
   * Wait until at least one credit is available (or starvation timeout).
   */
  waitForCredit(timeoutMs = 2000): Promise<void> {
    if (this.credits > 0) {
      this.consumeOne();
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const onCredit = (): void => {
        if (this.starvationTimer) {
          globalThis.clearTimeout(this.starvationTimer);
          this.starvationTimer = null;
        }
        this.consumeOne();
        resolve();
      };

      this.waiters.push(onCredit);

      this.starvationTimer = globalThis.setTimeout(() => {
        const idx = this.waiters.indexOf(onCredit);
        if (idx >= 0) {
          this.waiters.splice(idx, 1);
          printerDebugWarn('ISSC credit starvation — forcing 1 credit');
          this.grantInitialCredit(1);
          this.consumeOne();
          resolve();
        }
      }, timeoutMs);
    });
  }
}
